import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Printer, Download, WifiOff, HardDrive } from "lucide-react";
import { cacheLabels, getCachedLabels } from "@/lib/offlineDb";

// Avery template specs (mm). Page A4 = 210x297mm.
const TEMPLATES = {
  L7160: { id: "L7160", labels: 21, cols: 3, rows: 7, w: 63.5, h: 38.1, top: 15.1, left: 7.2, gap_x: 2.5, gap_y: 0.0, label: "Avery L7160 / 5160 (3×7)" },
  L7163: { id: "L7163", labels: 14, cols: 2, rows: 7, w: 99.1, h: 38.1, top: 15.1, left: 4.65, gap_x: 2.5, gap_y: 0.0, label: "Avery L7163 (2×7) — Large" },
  L7159: { id: "L7159", labels: 24, cols: 3, rows: 8, w: 63.5, h: 33.9, top: 13.5, left: 7.2, gap_x: 2.5, gap_y: 0.0, label: "Avery L7159 (3×8) — Compact" },
  "12L": { id: "12L",   labels: 12, cols: 2, rows: 6, w: 100.0, h: 44.0, top: 16.5, left: 5.0, gap_x: 0.0, gap_y: 0.0, label: "12L — 12 per A4 (100×44 mm) India" },
  "16L": { id: "16L",   labels: 16, cols: 2, rows: 8, w: 99.0,  h: 34.0, top: 12.5, left: 6.0, gap_x: 0.0, gap_y: 0.0, label: "16L — 16 per A4 (99×34 mm) India" },
};

function fmtMonthYear(my) {
  const [mm, yy] = (my || "").split("-");
  if (!mm || !yy) return my;
  const d = new Date(parseInt(yy), parseInt(mm) - 1, 1);
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}
function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("en-IN", { month: "short", year: "numeric" }); } catch { return s; }
}

export default function DispatchLabels() {
  const [params, setParams] = useSearchParams();
  const [orgs, setOrgs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [pers, setPers] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [pubId, setPubId] = useState("");
  const [perId, setPerId] = useState(params.get("periodical_id") || "");
  const today = new Date();
  const [issue, setIssue] = useState(params.get("issue") || `${String(today.getMonth() + 1).padStart(2, "0")}-${today.getFullYear()}`);
  const [tplId, setTplId] = useState("L7160");
  const [items, setItems] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null);  // { cached_at } when loaded from IndexedDB
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" ? !navigator.onLine : false);

  useEffect(() => {
    const on = () => setIsOffline(false);
    const off = () => setIsOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => { (async () => { try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch {} })(); }, []);
  useEffect(() => { if (!orgId) return; (async () => { try { const { data } = await api.get("/publication-houses/active-list", { params: { organization_id: orgId } }); setPubs(data); } catch {} })(); }, [orgId]);
  useEffect(() => { if (!pubId) return; (async () => { try { const { data } = await api.get("/periodicals", { params: { publication_house_id: pubId, page: 1, page_size: 100 } }); setPers(data.items.filter((x) => x.status === "active")); } catch {} })(); }, [pubId]);

  const load = async () => {
    if (!perId || !/^\d{2}-\d{4}$/.test(issue)) return;
    setItems(null); setCacheInfo(null);
    // Offline-first: if browser is offline, jump straight to IndexedDB cache.
    if (!navigator.onLine) {
      const cached = await getCachedLabels(perId, issue);
      if (cached) {
        setItems(cached.items);
        setCacheInfo({ cached_at: cached.cached_at });
        setParams({ periodical_id: perId, issue });
        toast.success(`Loaded ${cached.items.length} labels from offline cache`);
      } else {
        setItems([]);
        toast.error("No cached labels for this issue. Connect to the internet first.");
      }
      return;
    }
    try {
      const { data } = await api.get("/dispatch/labels", { params: { periodical_id: perId, issue_month_year: issue } });
      setItems(data.items);
      setParams({ periodical_id: perId, issue });
      // Cache for future offline use, fire-and-forget. Surface refresh time on the banner.
      const cachedAt = new Date().toISOString();
      cacheLabels(perId, issue, data.items, { cached_at: cachedAt }).catch(() => {});
      setCacheInfo({ cached_at: cachedAt, fresh: true });
    } catch (e) {
      // Network call failed but browser thinks it's online — try cache as fallback.
      const cached = await getCachedLabels(perId, issue);
      if (cached) {
        setItems(cached.items);
        setCacheInfo({ cached_at: cached.cached_at });
        toast.message(`Server unreachable · loaded ${cached.items.length} labels from cache`);
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
        setItems([]);
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [perId, issue]);

  const tpl = TEMPLATES[tplId];

  return (
    <div data-testid="dispatch-labels" className="mx-auto max-w-6xl">
      <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600 print:hidden">
        <Link to="/dispatch"><ArrowLeft size={14} className="mr-1" />Back to Dispatch</Link>
      </Button>

      <div className="print:hidden">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Print Labels</h1>
        <p className="mt-1 text-sm text-slate-500">Avery-standard A4 sheets, pincode-sorted (India Post bundling).</p>

        <div className="mt-6 grid grid-cols-1 gap-3 rounded-sm border border-slate-200 bg-white p-4 sm:grid-cols-4">
          <div>
            <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Organization</Label>
            <Select value={orgId} onValueChange={setOrgId}><SelectTrigger className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Pub House</Label>
            <Select value={pubId} onValueChange={setPubId} disabled={!orgId}><SelectTrigger className="rounded-sm"><SelectValue placeholder={orgId ? "Select" : "—"} /></SelectTrigger><SelectContent>{pubs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Periodical</Label>
            <Select value={perId} onValueChange={setPerId} disabled={!pubId}><SelectTrigger className="rounded-sm"><SelectValue placeholder={pubId ? "Select" : "—"} /></SelectTrigger><SelectContent>{pers.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Avery Template</Label>
            <Select value={tplId} onValueChange={setTplId}>
              <SelectTrigger className="rounded-sm" data-testid="lbl-tpl-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.values(TEMPLATES).map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-3">
            <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Issue (MM-YYYY)</Label>
            <Input value={issue} onChange={(e) => setIssue(e.target.value)} placeholder="06-2024" className="rounded-sm font-mono" />
          </div>
          <div className="flex items-end">
            <Button onClick={async () => {
              // Offline path: trigger browser print of the on-screen labels.
              if (!navigator.onLine) {
                if (!items || items.length === 0) { toast.error("Nothing to print"); return; }
                window.print();
                return;
              }
              try {
                const token = localStorage.getItem("access_token");
                const url = `${API_BASE}/dispatch/labels.pdf?periodical_id=${perId}&issue_month_year=${issue}&template=${tplId}`;
                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) { toast.error("Failed to download PDF"); return; }
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `labels-${issue}-${tplId}.pdf`;
                a.click();
                URL.revokeObjectURL(a.href);
              } catch (e) { toast.error(e.message); }
            }} data-testid="lbl-pdf-btn" disabled={!items || items.length === 0} className="w-full rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
              {isOffline ? <Printer size={14} className="mr-1.5" /> : <Download size={14} className="mr-1.5" />}
              {isOffline ? "Print Offline" : "Download PDF"}
            </Button>
          </div>
        </div>

        {cacheInfo && (
          <div data-testid="lbl-cache-banner" className={`mt-3 flex items-center gap-2 rounded-sm border px-3 py-2 text-xs ${isOffline ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"}`}>
            <HardDrive size={14} />
            <span>
              {isOffline ? <span className="font-semibold inline-flex items-center gap-1"><WifiOff size={11} />Offline · </span> : null}
              {isOffline
                ? "Loaded from local IndexedDB cache"
                : cacheInfo.fresh
                  ? "Cached locally for offline printing"
                  : "Loaded from local IndexedDB cache"} · last refreshed {new Date(cacheInfo.cached_at).toLocaleString("en-IN")}
            </span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="lbl-print-offline-btn"
              onClick={() => window.print()}
              disabled={!items || items.length === 0}
              className="ml-auto h-7 rounded-sm text-xs"
            >
              <Printer size={12} className="mr-1" /> Print labels offline
            </Button>
          </div>
        )}

        <div className="mt-3 rounded-sm border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-[#002FA7]">
          <span className="font-semibold">Server-rendered PDF</span> (Avery-accurate, pincode-sorted) — same engine the cron uses for headless exports. {items ? `${items.length} label${items.length !== 1 ? "s" : ""} ready.` : ""}
        </div>
      </div>

      {/* Print area */}
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body { background: white; }
          .no-print, .no-print * { display: none !important; }
        }
        .label-sheet { width: 210mm; min-height: 297mm; position: relative; background: white; box-sizing: border-box; }
        .label-grid { display: grid; gap: 0; padding-top: ${tpl.top}mm; padding-left: ${tpl.left}mm; grid-template-columns: repeat(${tpl.cols}, ${tpl.w}mm); grid-auto-rows: ${tpl.h}mm; column-gap: ${tpl.gap_x}mm; row-gap: ${tpl.gap_y}mm; }
        .label { width: ${tpl.w}mm; height: ${tpl.h}mm; padding: 2mm 3mm; box-sizing: border-box; font-family: 'IBM Plex Sans', system-ui, sans-serif; color: #0f172a; overflow: hidden; display: flex; flex-direction: column; justify-content: space-between; }
        .label-head { font-size: 7pt; text-transform: uppercase; letter-spacing: 0.04em; color: #475569; }
        .label-name { font-size: 10pt; font-weight: 600; }
        .label-addr { font-size: 8.5pt; line-height: 1.25; }
        .label-pin { font-size: 11pt; font-weight: 700; letter-spacing: 0.02em; }
        .label-foot { font-size: 7pt; color: #64748b; display: flex; justify-content: space-between; align-items: end; }
        .label-qr { font-family: monospace; font-size: 6.5pt; }
      `}</style>

      <div className="mt-6 print:m-0">
        {items && items.length > 0 ? (
          chunk(items, tpl.labels).map((page, pi) => (
            <div key={pi} className="label-sheet shadow print:shadow-none mb-4 print:mb-0 print:break-after-page mx-auto">
              <div className="label-grid">
                {page.map((d) => {
                  const a = d.address_snapshot || {};
                  return (
                    <div key={d.id} className="label">
                      <div className="label-head">{d.postage_class}{d.tracking_number ? ` · ${d.tracking_number}` : ""}</div>
                      <div>
                        <div className="label-name">{a.name}</div>
                        <div className="label-addr">{a.address_line_1}{a.address_line_2 ? ", " + a.address_line_2 : ""}</div>
                        <div className="label-addr">{a.landmark ? a.landmark + ", " : ""}{a.city}{a.state ? ", " + a.state : ""}</div>
                        <div className="label-pin">PIN {a.pincode}</div>
                      </div>
                      <div className="label-foot">
                        <span>Exp: {fmtDate(d.subscription_end_date_snapshot)}</span>
                        <span className="label-qr">{d.qr_code_string}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="rounded-sm border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500 print:hidden">
            {items === null ? "Loading…" : `No locked labels for ${fmtMonthYear(issue)}. Lock a dispatch first from the Dispatch page.`}
          </div>
        )}
      </div>
    </div>
  );
}

function chunk(arr, n) {
  const out = []; for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out;
}
